import { nextTick } from 'vue'
import { useRouter } from 'vue-router'
import qs from 'qs'
import { getLocationQuery } from '@/utils/helpers'
import { filterQueryFromUrl, mergeFilterQuery } from '@/utils/url-filter-query'
import { MAX_READ_TIME, ResourceType, TAG_MATCH } from '@/utils/constants'
import eventbus from '@/plugins/eventbus'

/**
 * Composable for bidirectional URL <-> filter synchronization.
 * Shared by useCollectionItemView and useCollectionContentView.
 *
 * @param {Object} options
 * @param {import('vue').ComputedRef} options.filters - Current filter values
 * @param {import('vue').ComputedRef} options.defaultFilterFn - Default filter factory
 * @param {Function} options.setFilters - Dispatch to set filters in store
 * @param {Object} [options.parseOptions]
 * @param {boolean} [options.parseOptions.stripIsFavorite=false] - Remove isFavorite from parsed URL params
 * @param {boolean} [options.parseOptions.validateResourceType=false] - Validate resourceType against ResourceType enum
 * @param {boolean} [options.parseOptions.coerceIsFavorite=false] - Coerce isFavorite string to boolean
 */
export const useUrlFilterSync = ({
  filters,
  defaultFilterFn,
  setFilters,
  parseOptions: {
    stripIsFavorite = false,
    validateResourceType = false,
    coerceIsFavorite = false,
  } = {},
} = {}) => {
  const router = useRouter()

  const setFiltersFromUrl = () => {
    const search = getLocationQuery()
    const defaults = defaultFilterFn.value()

    if (!search) {
      if (JSON.stringify(filters.value) !== JSON.stringify(defaults)) {
        setUrlFromFilters(true)
      }
      return
    }

    // Other query keys (the tier list's ?version=, ?action=) are not filters.
    const query = filterQueryFromUrl(qs.parse(search, { ignoreQueryPrefix: true, comma: true, parseBooleans: true }), Object.keys(defaults))

    if (coerceIsFavorite && defaults.isFavorite !== undefined && query.isFavorite) {
      query.isFavorite = true
    }

    if (query.tags && !Array.isArray(query.tags)) {
      delete query.tags
    }

    if (query.tagMatch && !Object.values(TAG_MATCH).includes(query.tagMatch)) {
      delete query.tagMatch
    }

    if (!Array.isArray(query.readTime) || query.readTime.length !== 2 || query.readTime[1] > MAX_READ_TIME) {
      delete query.readTime
    }

    if (!Array.isArray(query.language)) {
      delete query.language
    }

    if (!Array.isArray(query.sorting) || query.sorting.length !== 2) {
      delete query.sorting
    }

    if (Array.isArray(query.sorting)) {
      query.sorting[1] = query.sorting[1] === true || query.sorting[1] === 'true'
    }

    if (validateResourceType && query.resourceType && !Object.values(ResourceType).includes(query.resourceType)) {
      delete query.resourceType
    }

    const toUpdate = { ...filters.value, ...query }

    if (stripIsFavorite) {
      delete toUpdate.isFavorite
    }

    setFilters(toUpdate)

    nextTick(() => {
      eventbus.emit('collectionItems:filters:set-from-url')
    })
  }

  const setUrlFromFilters = (isFromUrlUpdate = false) => {
    if (isFromUrlUpdate) return

    const defaults = defaultFilterFn.value()
    const query = {}

    for (const [k, v] of Object.entries(filters.value)) {
      if (JSON.stringify(defaults[k]) !== JSON.stringify(v)) {
        query[k] = v
      }
    }

    const querystring = qs.stringify(query, { arrayFormat: 'comma', commaRoundTrip: true })
    const routerQuery = Object.fromEntries(new URLSearchParams(querystring).entries())

    router.replace({ query: mergeFilterQuery(router.currentRoute.value.query, routerQuery, Object.keys(defaults)) }).catch(() => {})
  }

  return {
    setFiltersFromUrl,
    setUrlFromFilters,
  }
}
